import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import {
  absolutizeReplicatedImageSrcs,
  legacyBlocksToSections,
  REPLICATED_IMAGE_PATH,
  type RpLayout,
} from '@mlm/shared';
import { RpPageRenderer, type RpRenderCtx } from '@mlm/ui';
import { API_BASE_URL, getReplicatedPage, replicatedBlockImageUrl } from '../lib/api';
import { getStoredRef } from '../lib/ref';
import { RollingCountdown } from '../components/rolling-countdown';

/** Wording used before the label was editable, and for any page missing one. */
const DEFAULT_CTA_LABEL = 'Sign up';

/**
 * Public replicated-link landing page. A prospect who opens a member's referral
 * link (`/r/:refCode`) lands here when the tenant enabled the custom page.
 *
 * This component is only a HOST: the page itself is drawn by `RpPageRenderer`
 * from `@mlm/ui`, the same component the admin builder previews with. That
 * sharing is the point — the pre-builder editor kept its own copy of the
 * renderer for previewing and it had already drifted from what visitors saw.
 *
 * What stays here is everything environment-shaped: how an image key becomes a
 * URL, how stored HTML is made safe, and the sponsor ref. The CTA's LABEL is
 * per-page and admin-editable; its DESTINATION is derived from the ref code +
 * campaign slug, so no campaign can be published that drops sponsor attribution.
 */
export function ReplicatedPage() {
  const { refCode, slug } = useParams();
  // `/c/:slug` (a storefront click on a virtual product) carries no ref in the
  // URL — fall back to the one captured from whatever link brought the visitor
  // here, so campaign signups keep their sponsor.
  const ref = refCode || getStoredRef();
  // Carry BOTH the sponsor ref AND the campaign slug to signup: `ref` preserves
  // sponsor attribution, `c` lets a tagged campaign narrow the lead's buyable
  // kits (server intersects the campaign's package tags with the sponsor tier's).
  const signupParams = new URLSearchParams();
  if (ref) signupParams.set('ref', ref);
  if (slug) signupParams.set('c', slug);
  const qs = signupParams.toString();
  const signupHref = `/signup${qs ? `?${qs}` : ''}`;

  // `?buy=<productId>` — set when a storefront click on a VIRTUAL product routed
  // here instead of the product page (`virtual_products_open_replicated_page`).
  // It names WHICH product sent the visitor, which the page can't otherwise know
  // (one campaign page may be assigned to several products). With it, the CTA
  // sells that product through `/buy/:productId` — which registers the visitor
  // if needed and then goes to checkout — instead of dropping a buyer on the
  // dashboard with no trace of what they clicked. Without it the CTA is
  // byte-identical to before.
  const [searchParams] = useSearchParams();
  const buyProductId = searchParams.get('buy')?.trim() || '';
  const ctaHref = buyProductId
    ? `/buy/${encodeURIComponent(buyProductId)}${qs ? `?${qs}` : ''}`
    : signupHref;

  const query = useQuery({
    queryKey: ['replicated-page', slug ?? ''],
    queryFn: () => getReplicatedPage(slug),
  });

  const ctx = useMemo<RpRenderCtx>(
    () => ({
      link: { ref: ref ?? undefined, campaignSlug: slug },
      pageId: query.data?.pageId ?? undefined,
      packages: (query.data?.packages ?? []).map((p) => ({
        ...p,
        // Product-image URLs come back origin-relative so they survive a
        // multi-domain tenant; absolutize against the API for rendering.
        imageUrl: p.imageUrl ? `${API_BASE_URL}${p.imageUrl}` : undefined,
      })),
      imageUrl: (key) => `${API_BASE_URL}${REPLICATED_IMAGE_PATH}?key=${encodeURIComponent(key)}`,
      legacyImageUrl: (blockId) => replicatedBlockImageUrl(blockId),
      // Absolutize stored origin-relative inline image srcs, then re-sanitize.
      // The API already sanitized at write time; DOMPurify here is
      // defense-in-depth. iframes must be explicitly allowed (DOMPurify strips
      // them by default) — the server already clamped their src to YouTube's
      // embed hosts.
      renderHtml: (html) =>
        DOMPurify.sanitize(absolutizeReplicatedImageSrcs(html, API_BASE_URL), {
          ADD_TAGS: ['iframe'],
          ADD_ATTR: ['allowfullscreen', 'frameborder', 'target'],
        }),
      mode: 'live',
    }),
    [ref, slug, query.data?.pageId, query.data?.packages],
  );

  // A page the API hasn't converted (an older API against a newer bundle) still
  // renders: convert its legacy blocks here with the same shared function.
  const layout: RpLayout | null = useMemo(() => {
    const d = query.data;
    if (!d) return null;
    if (d.layout) return d.layout;
    return d.blocks.length ? legacyBlocksToSections(d.blocks) : null;
  }, [query.data]);

  if (query.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  // Disabled, or no content → keep the legacy straight-to-signup behavior.
  if (!query.data?.enabled || !layout || layout.sections.length === 0) {
    return <Navigate to={ctaHref} replace />;
  }

  return (
    <RpPageRenderer
      layout={layout}
      ctx={ctx}
      header={
        query.data.timer ? (
          <RollingCountdown
            periodHours={query.data.timer.hours}
            label={query.data.timer.label}
            // Keyed by PAGE id — the default page is reachable both bare and
            // via its slug URL, and the anchor must survive both forms.
            storageKey={`mlm.rp.timer.${query.data.timer.pageId}`}
          />
        ) : null
      }
      footer={
        // `ctaAuto` defaults to true when absent (an older API), which matches
        // the pre-builder behavior of always appending the button.
        query.data.ctaAuto !== false ? (
          // The wrapper's classes are the pre-builder page's, so a converted
          // legacy page's CTA sits exactly where it always has.
          <div className="pt-2 text-center">
            <a
              href={ctaHref}
              className="inline-block rounded-md bg-primary px-8 py-3 text-base font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              {query.data.ctaLabel || DEFAULT_CTA_LABEL}
            </a>
          </div>
        ) : null
      }
    />
  );
}
